import { onBeforeUnmount, onMounted, toValue, watch } from 'vue'

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA'])
const RESIZE_RETRY_MS = [50, 300]

function isTextInput(el) {
  return Boolean(el && INPUT_TAGS.has(el.tagName))
}

function scrollFocusedInputIntoView(behavior = 'auto') {
  const target = document.activeElement
  if (!isTextInput(target)) {
    return
  }

  target.scrollIntoView({ block: 'nearest', behavior })
}

/**
 * Keeps focused text inputs visible above the soft keyboard (mobile PWA).
 * Same idea as BottomDrawer / UserChat: react to focus + visualViewport changes.
 *
 * @param {object} [options]
 * @param {import('vue').MaybeRefOrGetter<boolean>} [options.enabled] — when false, listeners stay idle
 */
export function useKeepInputAboveKeyboard(options = {}) {
  const { enabled = true } = options

  let bound = false
  let retryTimers = []

  function clearRetries() {
    retryTimers.forEach(clearTimeout)
    retryTimers = []
  }

  function scheduleScrollIntoView(behavior = 'auto') {
    clearRetries()
    requestAnimationFrame(() => scrollFocusedInputIntoView(behavior))
    retryTimers = RESIZE_RETRY_MS.map((ms) =>
      setTimeout(() => scrollFocusedInputIntoView('auto'), ms),
    )
  }

  function onFocusIn(event) {
    if (!toValue(enabled) || !isTextInput(event.target)) {
      return
    }
    scheduleScrollIntoView('smooth')
  }

  function onViewportChange() {
    if (!toValue(enabled) || !isTextInput(document.activeElement)) {
      return
    }
    scheduleScrollIntoView('auto')
  }

  function bind() {
    if (bound || typeof window === 'undefined') {
      return
    }
    bound = true
    document.addEventListener('focusin', onFocusIn)
    window.visualViewport?.addEventListener('resize', onViewportChange)
    window.visualViewport?.addEventListener('scroll', onViewportChange)
  }

  function unbind() {
    if (!bound) {
      return
    }
    bound = false
    clearRetries()
    document.removeEventListener('focusin', onFocusIn)
    window.visualViewport?.removeEventListener('resize', onViewportChange)
    window.visualViewport?.removeEventListener('scroll', onViewportChange)
  }

  function syncBinding() {
    if (toValue(enabled)) {
      bind()
    } else {
      unbind()
    }
  }

  onMounted(syncBinding)
  onBeforeUnmount(unbind)
  watch(() => toValue(enabled), syncBinding)

  return { scrollFocusedInputIntoView }
}
